import React from 'react';
import { PDFViewer } from '@react-pdf/renderer';

import ReportSummaryDoubleSided from './pages/report_summary/ReportSummaryDoubleSided';
import { OrganizationBrandData } from '_libs/app/types';
import { ReportDataType } from '_libs/data/types';
import Theme from '../../../src/models/theme';

interface FeaturesLogicCountProp {
  featureLogicId: number;
  count: number;
}
interface FeaturesCountProp {
  featureId: number;
  count: number;
  featureLogics: Record<number, FeaturesLogicCountProp>;
}
interface ThemesCountProp {
  themeId: number;
  count: number;
  features: Record<number, FeaturesCountProp>;
  prevalenceCount: number;
}

const DebugViewer = (): JSX.Element => {
  const coBrand: OrganizationBrandData = {
    id: 0,
    name: 'AMGA',
    placement: 'secondary',
    colors: {
      light: 'hsl(300, 100%, 97%)',
      primary: 'hsl(300, 100%, 50%)',
      dark: 'hsl(300, 100%, 8%)'
    },
    logoUrl: 'https://lb-jcl.s3.us-west-2.amazonaws.com/logos/1663817651988-logo-amga.png',
    logoHeight: 500,
    logoWidth: 500,
  };
  const lbBrand: OrganizationBrandData = {
    'id': 0,
    'name': 'Lightbeam Health Solutions',
    placement: 'tertiary',
    'colors': {
      'light': 'hsl(300, 100%, 97%)',
      'primary': 'hsl(300, 100%, 50%)',
      'dark': 'hsl(300, 100%, 8%)'
    },
    'logoUrl': 'https://lb-jcl.s3.us-west-2.amazonaws.com/logos/1666920972815-Lightbeam_Logo_-_Color.jpg',
    'logoHeight': 125,
    'logoWidth': 500,
  };
  const primaryBrand: OrganizationBrandData = {
    'id': 8082,
    'name': 'Adfinitas Health',
    'placement': 'primary',
    'colors': {
      'light': 'hsl(177, 100%, 97%)',
      'primary': 'hsl(177, 94%, 27%)',
      'dark': 'hsl(177, 100%, 8%)'
    },
    'logoUrl': 'https://lb-jcl.s3.us-west-2.amazonaws.com/campaigns/73/logos/dev-Adfinitas%2520Health.png',
    'logoHeight': 32,
    'logoWidth': 89,
  };
  const reportData: ReportDataType = {
    'count': 624,
    'household_est': 359192,
    'totalpopulation_est': 949507,
    'ep_age17_label': -1,
    'ep_age17_value': 22.53407189204503,
    'ep_age65_label': -1,
    'ep_age65_value': 11.579197415079616,
    'ep_limeng_label': -1,
    'ep_limeng_value': 2.6829101839164955,
    'ep_uninsur_label': -1,
    'ep_uninsur_value': -0.8009640792537601,
    'eqi_land_walkability_label': -1,
    'eqi_land_walkability_value': 10.0628,
    'ethnicity_hispanic': 0.10802107269893736,
    'plcr_cervical_label': -1,
    'plcr_cervical_value': 86.00815970814338,
    'plcr_colon_screen_label': -1,
    'plcr_colon_screen_value': 67.6899776410285,
    'plcr_mammouse_label': -1,
    'plcr_mammouse_value': 79.00962931289604,
    'poverty_belowpoverty_label': -1,
    'poverty_belowpoverty_value': 0.10233308139850472,
    'race_asian_pi': 0.08459614700872349,
    'race_black': 0.30782126107086527,
    'race_native_amer': 0.0028978554144698256,
    'race_other': 0.025711394209147496,
    'race_white': 0.5382040246390076,
    'NPIs': {
      'familyMedicine': 105,
      'generalPractice': 15,
      'internalMedicine': 171,
      'pediatricians': 80,
      'psychiatryNeurology': 94,
      'psychologist': 100
    },
    'NPIsMax': 171
  } as ReportDataType;
  const topThemes: ThemesCountProp[] = [
    {
      'themeId': 1,
      'count': 372,
      'features': {
        '151': {
          'featureId': 151,
          'count': 135,
          'featureLogics': {
            '538': {
              'featureLogicId': 538,
              'count': 135
            }
          }
        },
        '171': {
          'featureId': 171,
          'count': 237,
          'featureLogics': {
            '1066': {
              'featureLogicId': 1066,
              'count': 19
            },
            '1067': {
              'featureLogicId': 1067,
              'count': 218
            }
          }
        }
      },
      'prevalenceCount': 342
    },
    {
      'themeId': 24,
      'count': 251,
      'features': {
        '188': {
          'featureId': 188,
          'count': 251,
          'featureLogics': {
            '1208': {
              'featureLogicId': 1208,
              'count': 13
            },
            '1209': {
              'featureLogicId': 1209,
              'count': 238
            }
          }
        }
      },
      'prevalenceCount': 251
    },
    {
      'themeId': 22,
      'count': 198,
      'features': {
        '78': {
          'featureId': 78,
          'count': 8,
          'featureLogics': {
            '223': {
              'featureLogicId': 223,
              'count': 8
            }
          }
        },
        '79': {
          'featureId': 79,
          'count': 183,
          'featureLogics': {
            '224': {
              'featureLogicId': 224,
              'count': 183
            }
          }
        },
        '206': {
          'featureId': 206,
          'count': 7,
          'featureLogics': {
            '1264': {
              'featureLogicId': 1264,
              'count': 7
            }
          }
        }
      },
      'prevalenceCount': 197
    },
    {
      'themeId': 5,
      'count': 165,
      'features': {
        '174': {
          'featureId': 174,
          'count': 165,
          'featureLogics': {
            '1165': {
              'featureLogicId': 1165,
              'count': 165
            }
          }
        }
      },
      'prevalenceCount': 165
    },
    {
      'themeId': 30,
      'count': 85,
      'features': {
        '181': {
          'featureId': 181,
          'count': 85,
          'featureLogics': {
            '1187': {
              'featureLogicId': 1187,
              'count': 85
            }
          }
        }
      },
      'prevalenceCount': 85
    },
    {
      'themeId': 11,
      'count': 72,
      'features': {
        '21': {
          'featureId': 21,
          'count': 2,
          'featureLogics': {
            '23': {
              'featureLogicId': 23,
              'count': 2
            }
          }
        },
        '22': {
          'featureId': 22,
          'count': 69,
          'featureLogics': {
            '26': {
              'featureLogicId': 26,
              'count': 69
            }
          }
        },
        '77': {
          'featureId': 77,
          'count': 1,
          'featureLogics': {
            '211': {
              'featureLogicId': 211,
              'count': 1
            }
          }
        }
      },
      'prevalenceCount': 71
    }
  ] as unknown as ThemesCountProp[];
  const themeData: Theme[] = [
    {
      'id': 1,
      'name': 'Access to Care',
      'public_name': 'Barriers to Health Care',
      'header': 'Anything that prevents you or your family from accessing health care is a barrier.',
      'body_emphasis': 'In 2021, over 95% of Americans reported at least one barrier to health care.',
      'body': 'These barriers don\'t have to prevent you from achieving your best health.',
      'cta': 'Find out if there are local programs that can reduce obstacles you experience.',
      'cta_link': 'crsg.nl/r1',
      'qr_code_src': 'qr_1.png',
      'image_src': '01_national-cancer-institute-tl447mekwuQ-unsplash.jpg.jpg'
    },
    {
      'id': 2,
      'name': 'Access to Care (Behavioral Health)',
      'public_name': 'Mental Health',
      'header': 'Your mental health is as important as your physical health.',
      'body_emphasis': '1 in 4 Americans struggles with a mental health diagnosis.',
      'body': 'It\'s not easy to talk about mental health. But no matter how you\'re feeling, you aren\'t alone.',
      'cta': 'View trusted resources about the emotions we all face.',
      'cta_link': 'crsg.nl/r2',
      'qr_code_src': 'qr_2.png',
      'image_src': '02_k-mitch-hodge-IqSaG9zv2e0-unsplash.jpg.jpg'
    },
    {
      'id': 3,
      'name': 'Access to Care (for Minors)',
      'public_name': 'Health Care for Minors',
      'header': 'Lack of health care can impact your child\'s school success and future employment.',
      'body_emphasis': '20.3 million children face barriers to getting health care.',
      'body': 'Inconsistent health care can impact social-emotional development and academic achievement.',
      'cta': 'Learn more about the checkups your child may need.',
      'cta_link': 'crsg.nl/r3',
      'qr_code_src': 'qr_3.png',
      'image_src': '03_caleb-woods-VZILDYoqn_U-unsplash.jpg.jpg'
    },
    {
      'id': 4,
      'name': 'Air Pollution',
      'public_name': 'Air Pollution',
      'header': 'Lower levels of air pollution mean better heart and lung health in your community.',
      'body_emphasis': '4.2 million individuals across the globe die prematurely from outdoor air pollution',
      'body': ', according to the World Health Organization.',
      'cta': 'Find out what you can do to protect yourself from unhealthy air.',
      'cta_link': 'crsg.nl/r4',
      'qr_code_src': 'qr_4.png',
      'image_src': '04_johannes-plenio-5_9inhy4NSE-unsplash.jpg.jpg'
    },
    {
      'id': 5,
      'name': 'Asthma Services',
      'public_name': 'Asthma',
      'header': 'Individuals with asthma have difficulty moving air in and out of their lungs.',
      'body_emphasis': '1 in 13 people suffer from this condition in the United States.',
      'body': 'Managing your, or your child\'s, asthma doesn\'t have to be a challenge.',
      'cta': 'Learn about asthma triggers and other management approaches.',
      'cta_link': 'crsg.nl/r5',
      'qr_code_src': 'qr_5.png',
      'image_src': '05_Stocksy_txpcabb5910luX300_Medium_1209738.jpg'
    },
    {
      'id': 6,
      'name': 'CKD Services',
      'public_name': 'Kidney Disease',
      'header': 'Kidney disease is permanent damage to your kidneys that you cannot reverse.',
      'body_emphasis': '9 in 10 adults with chronic kidney disease do not know they have it.',
      'body': 'Prevent conditions that can cause damage, such as diabetes and high blood pressure.',
      'cta': 'Learn what can cause kidney disease and actions you can take to prevent it.',
      'cta_link': 'crsg.nl/r6',
      'qr_code_src': 'qr_6.png',
      'image_src': '06_robina-weermeijer-igwG8aIaypo-unsplash.jpg.jpg'
    },
    {
      'id': 7,
      'name': 'Community Services',
      'public_name': 'Community Services',
      'header': 'Social and community support is essential for maintaining physical and psychological health.',
      'body_emphasis': 'A 32% increase in the risk of stroke and a 29% increase in the risk of heart disease',
      'body': 'are linked to a lack of social support and poor social relationships.',
      'cta': 'Find out how you can connect to community organizations in your area.',
      'cta_link': 'crsg.nl/r7',
      'qr_code_src': 'qr_7.png',
      'image_src': '07_Stocksy_txpcabb5910luX300_Medium_4157153.jpg'
    },
    {
      'id': 8,
      'name': 'Dental Services',
      'public_name': 'Oral Health',
      'header': 'Poor oral health can lead to inflammation, infection, and disease.',
      'body_emphasis': '1 in 4 adults in the U.S. has untreated tooth decay.',
      'body': 'An unhealthy mouth can lead to heart disease, diabetes, arthritis, or poor maternal health.',
      'cta': 'Learn more about maintaining good oral health.',
      'cta_link': 'crsg.nl/r8',
      'qr_code_src': 'qr_8.png',
      'image_src': '08_caroline-lm-8BkF0sTC6Uo-unsplash.jpg.jpg'
    },
    {
      'id': 9,
      'name': 'Diabetes Services',
      'public_name': 'Diabetes',
      'header': 'Diabetes can cause eye problems, nerve damage, kidney disease, or heart disease.',
      'body_emphasis': '1 in 4 adults in the U.S. does not know they have diabetes.',
      'body': 'The good news is that prediabetes and type 2 diabetes are preventable with lifestyle changes.',
      'cta': 'Learn if you are at risk of prediabetes. If so, talk to your doctor about it.',
      'cta_link': 'crsg.nl/r9',
      'qr_code_src': 'qr_9.png',
      'image_src': '09_Stocksy_txpcabb5910luX300_Medium_571262.jpg'
    },
    {
      'id': 10,
      'name': 'Disability Services',
      'public_name': 'Disability Support',
      'header': 'Disabilities can make daily activities more challenging. And, a disability isn\'t always visible.',
      'body_emphasis': '1 in 4 adults in the United States has some disability.',
      'body': 'But having a disability does not mean that you\'re not healthy or cannot be healthy.',
      'cta': 'Learn more about healthy living with a disability.',
      'cta_link': 'crsg.nl/r10',
      'qr_code_src': 'qr_10.png',
      'image_src': '10_Stocksy_txpcabb5910luX300_Medium_4857910.jpg'
    },
    {
      'id': 11,
      'name': 'Health Literacy',
      'public_name': 'Health Literacy',
      'header': 'Health care services and information are vital for preventing illness and treating disease.',
      'body_emphasis': 'But nearly 9 in 10 adults in the United States struggle to navigate the healthcare system.',
      'body': 'Making decisions around your health should be easy.',
      'cta': 'Find health-related information to take control of your health today.',
      'cta_link': 'crsg.nl/r11',
      'qr_code_src': 'qr_11.png',
      'image_src': '11_element5-digital-OyCl7Y4y0Bk-unsplash.jpg.jpg'
    },
    {
      'id': 12,
      'name': 'Unemployment',
      'public_name': 'Unemployment and Health',
      'header': 'Job loss may lead to worse health, physiological stress, or financial stress.',
      'body_emphasis': '1 in 3 unemployed Americans references their poor physical and mental health due to joblessness.',
      'body': 'Has job loss affected your health?',
      'cta': 'You may qualify for health care coverage. Find out.',
      'cta_link': 'crsg.nl/r12',
      'qr_code_src': 'qr_12.png',
      'image_src': '12_Stocksy_txpcabb5910luX300_Medium_4037717.jpg'
    },
    {
      'id': 13,
      'name': 'Environmental Pollution',
      'public_name': 'Environmental Pollution',
      'header': 'Pollutants in the air, water, or land may cause breathing issues, heart disease, or certain cancers.',
      'body_emphasis': '1 in 6 Americans lives near toxic waste–often unknowingly.',
      'body': 'You can protect your household from potential environmental health hazards.',
      'cta': 'Learn how you can reduce your exposure to pollutants in your community.',
      'cta_link': 'crsg.nl/r13',
      'qr_code_src': 'qr_13.png',
      'image_src': '13_naja-bertolt-jensen-FxnqdmKBJps-unsplash.jpg.jpg'
    },
    {
      'id': 14,
      'name': 'Financial Services',
      'public_name': 'Financial Services',
      'header': 'Does the cost of health care prevent you from getting the care or medications you need?',
      'body_emphasis': '1 in 2 adults in the U.S. has difficulty affording their health care costs.',
      'body': 'Is health care too expensive for you, too?',
      'cta': 'Find help with getting the health care you need or want.',
      'cta_link': 'crsg.nl/r14',
      'qr_code_src': 'qr_14.png',
      'image_src': '14_connor-hall-r-HLeGnbY-8-unsplash.jpg.jpg'
    },
    {
      'id': 15,
      'name': 'Gaming and Alcohol',
      'public_name': 'Vice Consumption',
      'header': 'Changes in your lifestyle can improve your health, longevity, and mental status.',
      'body_emphasis': 'More than 1 in 2 Americans do not consider their lifestyle health',
      'body': 'and do not feel capable of improving their behavior on their own.',
      'cta': 'Find out how small changes today can lead to big changes in your long-term health.',
      'cta_link': 'crsg.nl/r15',
      'qr_code_src': 'qr_15.png',
      'image_src': '15_jonathan-petersson-a6N685qLsHQ-unsplash.jpg.jpg'
    },
    {
      'id': 16,
      'name': 'Heart Disease',
      'public_name': 'Heart Disease',
      'header': 'Every heartbeat matters. A healthy heart is a healthy life.',
      'body_emphasis': 'One person dies every 34 seconds from cardiovascular disease.',
      'body': 'Heart disease is the leading cause of death in the United States.',
      'cta': 'Learn if you are at risk of heart disease. If you are, let your provider know.',
      'cta_link': 'crsg.nl/r16',
      'qr_code_src': 'qr_16.png',
      'image_src': '16_kenny-eliason-MEbT27ZrtdE-unsplash.jpg.jpg'
    },
    {
      'id': 17,
      'name': 'High Cholesterol',
      'public_name': 'High Cholesterol',
      'header': 'Are you on the pathway to a healthy heart?',
      'body_emphasis': 'Too much cholesterol puts you at risk for heart disease and stroke.',
      'body': 'How do you know if you have high cholesterol? First, you have to check your cholesterol.',
      'cta': 'Talk to your healthcare team to get tested.',
      'cta_link': 'crsg.nl/r17',
      'qr_code_src': 'qr_17.png',
      'image_src': '17_zoran-borojevic-cvHxQExdKw8-unsplash.jpg.jpg.jpg'
    },
    {
      'id': 18,
      'name': 'Health Insurance',
      'public_name': 'Health Insurance',
      'header': 'The emergency room and the hospital do not need to be your primary source of healthcare.',
      'body_emphasis': '1 in 10 Americans is uninsured and more likely to experience worse health.',
      'body': 'Medical care is more likely to be delayed or avoided without coverage.',
      'cta': 'Learn how you can get access to the coverage you need.',
      'cta_link': 'crsg.nl/r18',
      'qr_code_src': 'qr_18.png',
      'image_src': '18_Stocksy_txpcabb5910luX300_Medium_3735701.jpg'
    },
    {
      'id': 19,
      'name': 'Internet Access',
      'public_name': 'Internet Access',
      'header': 'Are you online? Millions of Americans lack internet at home, making healthcare harder.',
      'body_emphasis': 'And 1 in 4 Americans lacks access to broadband internet.',
      'body': 'Using the internet to manage health care and access online resources may lead to better health.',
      'cta': 'You may qualify for a discount on your internet service.',
      'cta_link': 'crsg.nl/r19',
      'qr_code_src': 'qr_19.png',
      'image_src': '19_Stocksy_txpcabb5910luX300_Medium_1193452.jpg'
    },
    {
      'id': 20,
      'name': 'Mental Health',
      'public_name': 'Mental Health',
      'header': 'Mental health affects how we think, feel, and act.',
      'body_emphasis': 'Mental health determines how we handle stress, relate to others, and make healthy choices.',
      'body': 'Mental health is vital at every stage of life.',
      'cta': 'Learn if you or someone you love has unmet mental health needs.',
      'cta_link': 'crsg.nl/r20',
      'qr_code_src': 'qr_20.png',
      'image_src': '20_christopher-lemercier-12yvdCiLaVE-unsplash.jpg.jpg'
    },
    {
      'id': 21,
      'name': 'N/A',
      'public_name': 'MUST KEEP',
      'header': 'FOR FEATURE LOGICS',
      'body_emphasis': 'UNLESS RE-RUN FEATURE LOGICS',
      'body': '',
      'cta': '',
      'cta_link': 'crsg.nl/r21',
      'qr_code_src': 'qr_21.png',
      'image_src': null
    },
    {
      'id': 22,
      'name': 'Nutrition',
      'public_name': 'Healthy Food',
      'header': 'Unhealthy foods–high in saturated fats or sugar–increase the risk of severe health problems.',
      'body_emphasis': 'But eating a healthy diet may be difficult when access to nutritious food is limited.',
      'body': 'Do you have difficulty accessing healthy foods?',
      'cta': 'Find food pantries near you or learn simple healthy eating tips.',
      'cta_link': 'crsg.nl/r22',
      'qr_code_src': 'qr_22.png',
      'image_src': '22_nathan-dumlao-bRdRUUtbxO0-unsplash.jpg.jpg'
    },
    {
      'id': 23,
      'name': 'Physical Activity',
      'public_name': 'Physical Activity',
      'header': 'Regular activity reduces the risk of disease and improves the overall well-being of all ages.',
      'body_emphasis': '1 in 10 premature deaths could be prevented by getting enough physical activity.',
      'body': 'How do you move?',
      'cta': 'Learn more about the benefits of physical activity.',
      'cta_link': 'crsg.nl/r23',
      'qr_code_src': 'qr_23.png',
      'image_src': '23_fitsum-admasu-oGv9xIl7DkY-unsplash.jpg.jpg'
    },
    {
      'id': 24,
      'name': 'Mammograms',
      'public_name': 'Mammograms',
      'header': '40 is an important number. Around 40 to 44, women can start yearly screenings for breast cancer.',
      'body_emphasis': 'Early detection of breast cancer led to a 40% decrease in the death rate from breast cancer.',
      'body': 'Yearly screening saves lives!',
      'cta': 'Learn more about the warning signs of breast cancer.',
      'cta_link': 'crsg.nl/r24',
      'qr_code_src': 'qr_24.png',
      'image_src': '24_angiola-harry-nJv6xnlpNaA-unsplash.jpg.jpg'
    },
    {
      'id': 25,
      'name': 'Cancer Screenings',
      'public_name': 'Cancer Screening',
      'header': 'Have you delayed seeking care for cancer-related symptoms?',
      'body_emphasis': 'You are not alone.',
      'body': 'But every delay in timely cancer care is associated with an increased risk of death.',
      'cta': 'Don\'t wait. Learn about when to seek appropriate medical care.',
      'cta_link': 'crsg.nl/r25',
      'qr_code_src': 'qr_25.png',
      'image_src': '25_national-cancer-institute-ct10qdGv1hQ-unsplash.jpg.jpg'
    },
    {
      'id': 26,
      'name': 'Cervical Cancer Screenings',
      'public_name': 'Cervical Cancer',
      'header': 'Human papillomavirus (HPV) causes almost all cervical cancer.',
      'body_emphasis': 'But 1 in 4 American women is not up-to-date on cervical cancer screening.',
      'body': 'The most important thing you can do is to have regular screening tests.',
      'cta': 'Learn more about the screening and when to get it.',
      'cta_link': 'crsg.nl/r26',
      'qr_code_src': 'qr_26.png',
      'image_src': '26_national-cancer-institute-jdfn7Z03Qa4-unsplash.jpg.jpg'
    },
    {
      'id': 27,
      'name': 'Cholesterol Screenings',
      'public_name': 'Cholesterol Screening',
      'header': 'Too much cholesterol puts you at risk for heart disease and stroke.',
      'body_emphasis': 'About 102 million adults in the United States have total cholesterol that is higher than desired.',
      'body': 'Did you know high cholesterol is preventable?',
      'cta': 'Learn how you can have your cholesterol checked today.',
      'cta_link': 'crsg.nl/r27',
      'qr_code_src': 'qr_27.png',
      'image_src': '27_eiliv-aceron-M-pEl_nv1Q0-unsplash.jpg.jpg'
    },
    {
      'id': 28,
      'name': 'Colorectal Cancer Screenings',
      'public_name': 'Colonoscopies',
      'header': 'Timely screening tests can help identify cancers at an earlier stage.',
      'body_emphasis': 'Colorectal cancer is the 2nd leading cause of death in the U.S.',
      'body': ', and about 1 in 3 adults are not up to date on preventative colorectal cancer screening.',
      'cta': 'Learn more about how colorectal cancer screenings save lives.',
      'cta_link': 'crsg.nl/r28',
      'qr_code_src': 'qr_28.png',
      'image_src': '28_national-cancer-institute-L7en7Lb-Ovc-unsplash.jpg.jpg'
    },
    {
      'id': 29,
      'name': 'Screenings for Older Men',
      'public_name': 'Screenings for Older Men',
      'header': 'Getting preventative services reduces the risk of disease, disability, and death.',
      'body_emphasis': 'Men are 2 times less likely than women to use preventive health services.',
      'body': 'Take charge of your health, and don\'t put off the recommended screenings.',
      'cta': 'Don\'t delay! Learn more about healthy aging.',
      'cta_link': 'crsg.nl/r29',
      'qr_code_src': 'qr_29.png',
      'image_src': '29_cdc-j_g42ZMeyzs-unsplash.jpg.jpg'
    },
    {
      'id': 30,
      'name': 'Screenings for Older Women',
      'public_name': 'Screenings for Older Women',
      'header': 'Regular checkups with your healthcare provider can help you avoid problems in the future.',
      'body_emphasis': '23% of women last saw a physician a year ago. ',
      'body': 'Preventative care can help spot a health condition before it becomes serious.',
      'cta': 'Don\'t delay! Learn more about healthy aging.',
      'cta_link': 'crsg.nl/r30',
      'qr_code_src': 'qr_30.png',
      'image_src': '30_national-cancer-institute-0izFVmwJ5pw-unsplash.jpg.jpg'
    },
    {
      'id': 31,
      'name': 'Routine Care',
      'public_name': 'Annual Checkup',
      'header': 'Have you missed your annual checkup? You\'re not alone.',
      'body_emphasis': '38% of Americans do not get an annual physical.',
      'body': 'But annual checkups can identify potential problems before they become serious.',
      'cta': 'Learn more about routine checkups you or a loved one may have missed.',
      'cta_link': 'crsg.nl/r31',
      'qr_code_src': 'qr_31.png',
      'image_src': '31_cdc-kSB935wtwEA-unsplash.jpg.jpg'
    },
    {
      'id': 32,
      'name': 'Services for Minors',
      'public_name': 'Healthcare for Minors',
      'header': 'Health care impacts a child\'s physical and emotional health, growth, and development.',
      'body_emphasis': 'Nearly half a million children in the U.S. have fair to poor health.',
      'body': 'Health care as a child impacts their capacity to manage their health as an adult.',
      'cta': 'Learn more about this and other topics about children\'s health.',
      'cta_link': 'crsg.nl/r32',
      'qr_code_src': 'qr_32.png',
      'image_src': '32_cdc-8nL4sGMz5vU-unsplash.jpg.jpg'
    },
    {
      'id': 33,
      'name': 'Services for Older Adults',
      'public_name': 'Services for Older Adults',
      'header': 'Social isolation and loneliness significantly impact physical and mental health.',
      'body_emphasis': 'Over 25% of adults over 65 feel socially isolated. 30% feel lonely.',
      'body': 'Loneliness and isolation can lead to various diseases, including Alzheimer\'s.',
      'cta': 'Are you at risk of feeling lonely or socially isolated? Take the assessment.',
      'cta_link': 'crsg.nl/r33',
      'qr_code_src': 'qr_33.png',
      'image_src': '33_noah-silliman-gzhyKEo_cbU-unsplash.jpg.jpg'
    },
    {
      'id': 34,
      'name': 'Smoking Cessation',
      'public_name': 'Smoking',
      'header': 'Quitting smoking lowers the risk of cancer, cardiovascular disease, and respiratory disease.',
      'body_emphasis': '34 million adults in the US currently smoke cigarettes.',
      'body': 'Many treatments may be free of charge or covered by your insurance.',
      'cta': 'Learn how you can access resources to quit today.',
      'cta_link': 'crsg.nl/r34',
      'qr_code_src': 'qr_34.png',
      'image_src': '34_pawel-czerwinski-7USMFYqt1NI-unsplash.jpg.jpg'
    },
    {
      'id': 35,
      'name': 'Spanish-Language Support',
      'public_name': 'Spanish Speakers',
      'header': 'Do you need a doctor who speaks Spanish?',
      'body_emphasis': '66% of Americans have trouble accessing healthcare due to their native language.',
      'body': 'Clear communication with your doctor is critical.',
      'cta': 'Learn what questions you can ask your doctor.',
      'cta_link': 'crsg.nl/r35',
      'qr_code_src': 'qr_35.png',
      'image_src': '35_volodymyr-hryshchenko-V5vqWC9gyEU-unsplash.jpg.jpg'
    },
    {
      'id': 36,
      'name': 'Substance Use',
      'public_name': 'Substance Misuse',
      'header': 'Too much alcohol can lead to injuries, violence, liver disease, high blood pressure, and cancer.',
      'body_emphasis': '1 in 6 adults binge drink, with 25% binge drinking weekly.',
      'body': 'You can take control of your drinking before it starts to impact your health.',
      'cta': 'Learn how.',
      'cta_link': 'crsg.nl/r36',
      'qr_code_src': 'qr_36.png',
      'image_src': '36_reza-heydar-3HOhap6CQWw-unsplash.jpg.jpg'
    },
    {
      'id': 37,
      'name': 'Transportation',
      'public_name': 'Transportation',
      'header': 'Delaying care can increase the risk of undiagnosed disease, worsening conditions–even death.',
      'body_emphasis': '5.8 million Americans delay medical care due to a lack of transportation.',
      'body': 'Get healthy and stay healthy by using transportation services in your area.',
      'cta': 'Need a ride? See which transportation services are available in your area.',
      'cta_link': 'crsg.nl/r37',
      'qr_code_src': 'qr_37.png',
      'image_src': '37_marc-olivier-jodoin-NqOInJ-ttqM-unsplash.jpg.jpg'
    },
    {
      'id': 38,
      'name': 'Walkability',
      'public_name': 'Walking',
      'header': 'Do you have trouble staying physically active in your neighborhood?',
      'body_emphasis': 'Lack of physical exercise, like walking,',
      'body': 'can lead to heart disease, obesity, high cholesterol, and type 2 diabetes.',
      'cta': 'Find community-based exercise programs or build an activity plan.',
      'cta_link': 'crsg.nl/r38',
      'qr_code_src': 'qr_38.png',
      'image_src': '38_ben-white-JJ9irt1OZmI-unsplash.jpg.jpg'
    },
    {
      'id': 39,
      'name': 'Water Pollution',
      'public_name': 'Water Pollution',
      'header': 'Safe drinking water is essential to your health and household.',
      'body_emphasis': 'Water pollution kills 1.8 million individuals in the United States each year.',
      'body': 'How clean is your water?',
      'cta': 'Learn more about your drinking water and if it\'s safe to drink. ',
      'cta_link': 'crsg.nl/r39',
      'qr_code_src': 'qr_39.png',
      'image_src': '39_naja-bertolt-jensen-FxnqdmKBJps-unsplash.jpg.jpg'
    }
  ] as Theme[];

  const data = {
    coBrand,
    lbBrand,
    primaryBrand,
    radius: 10,
    reportData,
    topThemes,
    themeData,
    clinicalGuideStats: null,
    zipCode: '21076',
  };
  return (
    <>
      <PDFViewer
        showToolbar={false}
        className="mt-4 mb-4"
        style={{
          width: '100%',
          height: '90vh',
        }}
      >
        <ReportSummaryDoubleSided {...data} />
        {/* <HimssSummaryPage data={null} /> */}
      </PDFViewer>
    </>
  );
};

export default DebugViewer;
